import React, { useRef } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import styled, { css } from 'styled-components';
import {
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  brandColours,
  standardTransition,
  visuallyHidden,
  zIndexLayers,
  fontWeights,
} from '../styles';
import { Slider, Button, Svg } from './ui';
import SubBanner from './SubBanner';
import arrowIcon from '../images/slider-arrow.inline.svg';

const StyledImageContentBannerSlider = styled.section`
  overflow: hidden;
`;

const StyledOuter = styled.div`
  position: relative;
`;

const StyledSideHeading = styled.h2`
  font-weight: ${fontWeights.regular};
  text-transform: uppercase;
  letter-spacing: 0.3em;
  color: ${standardColours.darkGrey};
  z-index: ${zIndexLayers.first};
  text-align: center;
  margin-bottom: 20px;
  padding: 0 20px;

  ${minBreakpointQuery.small`
    position: absolute;
    top: 60%;
    left: 10%;
    font-weight: ${fontWeights.regular};
    text-transform: uppercase;
    letter-spacing: 0.3em;
    transform: rotate(-90deg);
    transform-origin: 0% 50%;
    color: ${standardColours.white};
    z-index: ${zIndexLayers.first};
  `}

  ${minBreakpointQuery.medium`
    left: 5%;
  `}

  ${minBreakpointQuery.large`
    top: 55%;
  `}
`;

const StyledSlider = styled(Slider)`
  position: relative;
  --pagination-gap: 10px;

  .swiper-pagination {
    margin: 0 auto;
    background: ${standardColours.white};
    position: absolute;
    max-width: fit-content;
    left: 50%;
    bottom: 120px;
    transform: translateX(-50%);
    padding: 8px;
    border-radius: 40px;
    display: flex;
    justify-content: center;

    ${minBreakpointQuery.medium`
      bottom: 40px;
    `}
  }

  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background-color: ${brandColours.primary};
    opacity: 1;
    transition: ${standardTransition('background-color')};

    ${minBreakpointQuery.small`
      width: 8px;
      height: 8px;
    `}
  }

  .swiper-pagination-bullet-active {
    background-color: ${standardColours.darkGrey};
  }

  ${maxBreakpointQuery.xxxxlarge`
    overflow: visible;
  `}

  ${minBreakpointQuery.small`
    --pagination-gap: 15px;
  `}

  ${minBreakpointQuery.large`
    --pagination-gap: 20px;
  `}
`;

const StyledButton = styled(Button)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  border-radius: 0;
  z-index: ${zIndexLayers.first};

  ${minBreakpointQuery.tsmall`
    max-width: 400px;  
  `}
`;

const StyledButtonWrapper = styled.div`
  position: absolute;
  right: 100px;
  bottom: 0;
  z-index: ${zIndexLayers.first};
  display: flex;
`;

const StyledArrowButton = styled.button`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    z-index: ${zIndexLayers.first};
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background-color: ${standardColours.transparent};
  `}

  &:disabled {
    ${minBreakpointQuery.mlarge`
      ${visuallyHidden()};
    `}
  }

  svg {
    ${minBreakpointQuery.mlarge`
      width: 100px;
      height: 100px;
      fill: ${standardColours.white};
    `}
  }

  &:focus {
    svg {
      ${minBreakpointQuery.mlarge`
        fill: ${brandColours.primary};
      `}
    }
  }

  ${({ $next }) => {
    if ($next) {
      return css`
        ${minBreakpointQuery.xxxlarge`
          right: 0;
        `}

        ${minBreakpointQuery.xxxxlarge`
          right: -12.5px;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.mlarge`
          transform: rotate(180deg);
        `}

        ${minBreakpointQuery.xxxlarge`
          left: 0;
        `}

        ${minBreakpointQuery.xxxxlarge`
          left: -12.5px;
        `}
      `;
    }
  }}
`;

const ImageContentBannerSlider = ({ heading, items, link }) => {
  const prevEl = useRef(null);
  const nextEl = useRef(null);

  const sliderOptions = {
    modules: [Navigation, Pagination],
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    pagination: { clickable: true },
    navigation: {
      prevEl: prevEl.current,
      nextEl: nextEl.current,
    },
    onInit: swiper => {
      swiper.params.navigation.prevEl = prevEl.current;
      swiper.params.navigation.nextEl = nextEl.current;
      swiper.navigation.init();
      swiper.navigation.update();
    },
  };

  return (
    items.length > 0 && (
      <StyledImageContentBannerSlider>
        <StyledOuter>
          {heading && <StyledSideHeading>{heading}</StyledSideHeading>}
          <StyledSlider {...sliderOptions}>
            {items.map(({ overline, heading, text, image }, id) => (
              <SubBanner
                key={id}
                overline={overline}
                heading={heading}
                text={text}
                image={image}
                isImageContentBannerSlider={true}
              />
            ))}
          </StyledSlider>
          {link && (
            <StyledButton
              to={link.pageUrl}
              isAlt={true}
              isLarge={true}
              hasArrow={true}
            >
              {link.text}
            </StyledButton>
          )}
          <StyledButtonWrapper>
            <StyledArrowButton ref={prevEl} aria-label="Previous">
              <Svg image={arrowIcon} />
            </StyledArrowButton>
            <StyledArrowButton ref={nextEl} $next={true} aria-label="Next">
              <Svg image={arrowIcon} />
            </StyledArrowButton>
          </StyledButtonWrapper>
        </StyledOuter>
      </StyledImageContentBannerSlider>
    )
  );
};

export default ImageContentBannerSlider;
